import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Grid, IconButton } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import {
  ButtonWithLoader,
  type CustomFieldValidatorState,
  ErrorTooltip,
  type Field,
  Text,
  useFields,
  Validators,
} from '@pbt/pbt-ui-components'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@pbt/pbt-ui-components/src/icons'

import type { Clinic } from '~/api/graphql/generated/types'

import AddNewPracticeForm from './AddNewPracticeForm'
import { ClinicBrief } from './ClinicBrief'
import ClinicSearch from './ClinicSearch'

const useStyles = makeStyles(
  (theme) => ({
    sectionHeading: {
      fontSize: '1.4rem',
    },
    errorTooltip: {
      width: 'fit-content',
      maxWidth: 'none',
    },
    addAnotherButton: {
      marginTop: theme.spacing(2),
      width: '50%',
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
      },
    },
    clinicItem: {
      marginBottom: theme.spacing(1),
    },
    clinicItemWrapper: {
      display: 'inline-flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
    },
    actionIcon: {
      padding: 0,
      color: theme.colors.grayGray1,
      '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
        borderRadius: 4,
      },
    },
  }),
  { name: 'PreviousPracticesForm' },
)

export interface PreviousPracticesFormProps {
  previousClinics: Field
}

const PreviousPracticesForm = ({
  previousClinics,
}: PreviousPracticesFormProps) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'TimeTable'])

  const [isAddNewPracticeFormOpen, setIsAddNewPracticeFormOpen] =
    useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(
    previousClinics.value.length === 0,
  )
  const [isSearching, setIsSearching] = useState(false)

  const validatePreviousPracticeName = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validateStreetAddress = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validateCity = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validateState = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const validatePhoneNumber = ({ value }: CustomFieldValidatorState) =>
    !isAddNewPracticeFormOpen || Validators.notEmptyFormField(value)

  const {
    fields: formFields,
    reset: resetFormFields,
    validate: validateFormFields,
  } = useFields(
    [
      {
        name: 'previousPracticeName',
        label: t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICE_NAME_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validatePreviousPracticeName,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_PRACTICE_NAME_ERROR'),
        },
      },
      {
        name: 'streetAddress',
        label: t('TimeTable:PMR_CONSENT.STREET_ADDRESS_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validateStreetAddress,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t(
            'TimeTable:PMR_CONSENT.PLEASE_ENTER_STREET_ADDRESS_ERROR',
          ),
        },
      },
      {
        name: 'aptSuiteUnitBuilding',
        label: t('TimeTable:PMR_CONSENT.APT_SUITE_UNIT_BUILDING_LABEL'),
        initialValue: '',
      },
      {
        name: 'city',
        label: t('TimeTable:PMR_CONSENT.CITY_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validateCity,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_CITY_ERROR'),
        },
      },
      {
        name: 'state',
        label: t('TimeTable:PMR_CONSENT.STATE_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validateState,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_STATE_ERROR'),
        },
      },
      {
        name: 'phoneNumber',
        label: t('TimeTable:PMR_CONSENT.PHONE_NUMBER_LABEL'),
        initialValue: '',
        validators: [
          {
            validator: validatePhoneNumber,
            validatorName: 'required',
          },
        ],
        messages: {
          required: t('TimeTable:PMR_CONSENT.PLEASE_ENTER_PHONE_NUMBER_ERROR'),
        },
      },
      {
        name: 'emailAddress',
        label: t('TimeTable:PMR_CONSENT.EMAIL_ADDRESS_LABEL'),
        initialValue: '',
      },
    ],
    false,
  )

  const {
    aptSuiteUnitBuilding,
    city,
    emailAddress,
    phoneNumber,
    previousPracticeName,
    state,
    streetAddress,
  } = formFields

  const handleSelectClinic = (clinic: Clinic) => {
    const currentClinics = previousClinics.value as Clinic[]
    if (!currentClinics.some((c) => c.hin === clinic.hin)) {
      // Replace with .id if we can query it
      previousClinics.setValue([...currentClinics, clinic])
    }
    setIsSearchOpen(false)
    setIsSearching(false)
  }

  const handleAddAnotherPractice = () => {
    setIsSearchOpen(true)
  }

  const handleResultsVisibilityChange = (visible: boolean) => {
    setIsSearching(visible)
  }

  const handleOpenForm = () => {
    setIsSearchOpen(false)
    setIsAddNewPracticeFormOpen(true)
  }

  const handleAddPractice = async () => {
    setIsSearching(false)
    if (validateFormFields()) {
      const newClinic: Partial<Clinic> = {
        name: previousPracticeName.value,
        phone: phoneNumber.value,
        address: {
          street1: streetAddress.value,
          street2: aptSuiteUnitBuilding.value || undefined,
          city: city.value,
          state: state.value,
          // TODO: Add email but we don't have that on the Clinic object yet
        },
      }
      const currentClinics = previousClinics.value as Partial<Clinic>[]
      previousClinics.setValue([...currentClinics, newClinic])
      resetFormFields()
      setIsAddNewPracticeFormOpen(false)
    }
  }

  const handleCancelAddPractice = () => {
    resetFormFields()
    setIsAddNewPracticeFormOpen(false)
  }

  const handleDeleteClinic = (index: number) => {
    const currentClinics = previousClinics.value as Partial<Clinic>[]
    previousClinics.setValue(currentClinics.filter((_, i) => i !== index))
  }

  const handleEditClinic = (index: number) => {
    handleDeleteClinic(index)
    setIsSearchOpen(true)
  }

  useEffect(() => {
    if (
      isAddNewPracticeFormOpen &&
      previousClinics.open &&
      !previousClinics.valid
    ) {
      validateFormFields()
    }
  }, [
    isAddNewPracticeFormOpen,
    previousClinics.open,
    previousClinics.valid,
    validateFormFields,
  ])

  const clinicsList = previousClinics.value as Clinic[]

  return (
    <Grid container direction="column" mb={3}>
      {(!isAddNewPracticeFormOpen || clinicsList.length > 0) && (
        <Grid item>
          <Text className={classes.sectionHeading} pb={1} variant="h2">
            {clinicsList.length > 0
              ? t('TimeTable:PMR_CONSENT.PREVIOUS_PRACTICES_TITLE')
              : t('TimeTable:PMR_CONSENT.FIND_PREVIOUS_PRACTICE_TITLE')}
          </Text>
        </Grid>
      )}
      {clinicsList.length > 0 && (
        <Grid container direction="column">
          {clinicsList.map((clinic, index) => (
            <Grid
              item
              className={classes.clinicItem}
              key={clinic.hin ?? `clinic-${index}`}
            >
              <div className={classes.clinicItemWrapper}>
                <ClinicBrief clinic={clinic} />
                <IconButton
                  aria-label={t('Common:EDIT_CLINIC')}
                  className={classes.actionIcon}
                  size="small"
                  onClick={() => handleEditClinic(index)}
                >
                  <EditIcon />
                </IconButton>
                {clinicsList.length > 1 && (
                  <IconButton
                    aria-label={t('Common:DELETE_CLINIC')}
                    className={classes.actionIcon}
                    size="small"
                    onClick={() => handleDeleteClinic(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </div>
            </Grid>
          ))}
        </Grid>
      )}
      {!isAddNewPracticeFormOpen && isSearchOpen && (
        <ErrorTooltip
          classes={{
            errorTooltip: classes.errorTooltip,
          }}
          message={previousClinics.message}
          open={previousClinics.open && !previousClinics.valid && !isSearching} // !isSearching so it doesn't appear ontop of search results list
          placement="bottom"
        >
          <Grid item>
            <ClinicSearch
              onAddNewPractice={handleOpenForm}
              onResultsVisibilityChange={handleResultsVisibilityChange}
              onSelectClinic={handleSelectClinic}
            />
          </Grid>
        </ErrorTooltip>
      )}
      {!isAddNewPracticeFormOpen && !isSearchOpen && (
        <ButtonWithLoader
          className={classes.addAnotherButton}
          color="secondary"
          onClick={handleAddAnotherPractice}
        >
          {t('TimeTable:PMR_CONSENT.ADD_ANOTHER_PRACTICE')}
        </ButtonWithLoader>
      )}
      {isAddNewPracticeFormOpen && (
        <AddNewPracticeForm
          fields={{
            aptSuiteUnitBuilding,
            city,
            emailAddress,
            phoneNumber,
            previousPracticeName,
            state,
            streetAddress,
          }}
          isFormOpen={isAddNewPracticeFormOpen}
          isValidationTriggeredExternally={
            previousClinics.open && !previousClinics.valid
          }
          onCancel={handleCancelAddPractice}
          onSubmit={handleAddPractice}
        />
      )}
    </Grid>
  )
}

export default PreviousPracticesForm
